// Staggered entrance for lists and page sections.
//
//   <Reveal index={i}>…</Reveal>                       // div, 35ms * i delay
//   <Reveal as="li" index={i} className="…">…</Reveal>
//   <tr {...revealProps(i)}>                            // spread onto any element
//   const next = createRevealSequence(); <div {...next()}>  // mixed content, auto-numbered
//
// Delay is capped so long lists don't keep trickling in; reduced-motion users
// get no animation (see .reveal in index.css).
import { createElement, CSSProperties, ElementType, HTMLAttributes, ReactNode } from "react";
import cn from "classnames";

export const REVEAL_STEP_MS = 35;
export const REVEAL_MAX_STEPS = 24;

export function revealProps(index = 0, step = REVEAL_STEP_MS): { className: string; style: CSSProperties } {
  return { className: "reveal", style: { animationDelay: `${Math.min(Math.max(index, 0), REVEAL_MAX_STEPS) * step}ms` } };
}

// For pages that interleave headings, cards and lists: call next() once per
// element in render order and each gets the following slot.
export function createRevealSequence(step = REVEAL_STEP_MS) {
  let i = 0;
  return () => revealProps(i++, step);
}

type RevealProps<T extends ElementType> = {
  as?: T;
  index?: number;
  step?: number;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
} & Omit<HTMLAttributes<HTMLElement>, "className" | "style" | "children">;

export function Reveal<T extends ElementType = "div">({ as, index = 0, step = REVEAL_STEP_MS, className, style, children, ...rest }: RevealProps<T>) {
  const p = revealProps(index, step);
  return createElement(as || "div", { ...rest, className: cn(p.className, className), style: { ...p.style, ...style } }, children);
}

export default Reveal;
